import { CSSProperties, MouseEventHandler } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';

import Icon from '@/common/components/Icon/Icon';
import { useAppStore } from '@/common/stores/AppStore';
import { MediaSize } from '@/media/constants/sizeCodes';
import { MEDIA_SIZE_MAP } from '@/media/constants/sizeCodeToPx';
import { MEDIA_SIZE_160 } from '@/media/constants/sizes';
import { ProductEntity } from '@/product/types';

import Media from '../../entity/Media';
import MediaSource from '../../entity/MediaSource';
import Image from '../Image/Image';
import MediaPlaceholder from '../MediaPlaceholder/MediaPlaceholder';
import { getProductShapeIcon } from '../MediaPlaceholder/MediaPlaceholder.utils';
import RenderLabel from '../RenderLabel/RenderLabel';

import { MEDIA_PREVIEW_SIZE_CLASS_MAP } from './MediaPreview.constants';
import styles from './MediaPreview.module.css';

interface MediaPreviewProps {
  product: ProductEntity;
  media?: Nullable<Media>;
  mediaSource?: Nullable<MediaSource>;
  className?: string;
  mediaSize: Nullable<MediaSize>;
  active?: boolean;
  showType?: boolean;
  isSingleScale?: Nullable<boolean>;
  isGrayscale?: Nullable<boolean>;
  isSaturation?: Nullable<boolean>;
  index?: number;
  onClick?: MouseEventHandler<HTMLDivElement>;
  maxScale?: Nullable<number>;
  containerSize?: MediaSize;
  style?: CSSProperties;
  previewFrame?: number;
  isRenderLabelCompact?: boolean;
  isRenderLabelHidden?: boolean;
}

function MediaPreview(props: MediaPreviewProps) {
  const {
    media,
    mediaSource,
    className,
    index = 0,
    active = false,
    mediaSize,
    showType = false,
    isSingleScale = true,
    maxScale,
    isGrayscale = false,
    containerSize,
    product,
    isSaturation = false,
    onClick,
    style: customStyle,
    previewFrame,
    isRenderLabelCompact = false,
    isRenderLabelHidden = false,
  } = props;

  const usedSize = containerSize || mediaSize;
  const shouldRenderMedia = Boolean(media && mediaSource && !mediaSource.isEmpty);
  const { isHQModeEnabled } = useAppStore();

  if (shouldRenderMedia) {
    const style: CSSProperties = {
      ...(customStyle || {}),
      backgroundColor: media!.bgColor!,
      order: index,
    };

    return (
      <div
        className={cls(styles.mediaPreview, usedSize && MEDIA_PREVIEW_SIZE_CLASS_MAP[usedSize], className, {
          [styles.mediaPreviewActive]: active,
        })}
        onClick={onClick}
        style={style}
        aria-label="Media preview"
      >
        {media!.isVideo && showType && (
          <div className={styles.mediaPreviewVideoFlag}>
            <Icon name="play" />
          </div>
        )}

        <Image
          containerSize={usedSize}
          hqMode={isHQModeEnabled}
          isGrayscale={isGrayscale}
          isSaturation={isSaturation}
          isSingleScale={isSingleScale}
          maxScale={maxScale}
          media={media}
          mediaSize={mediaSize}
          mediaSource={mediaSource}
          previewFrame={previewFrame}
        />

        {mediaSource!.isRender && !isRenderLabelHidden && (
          <RenderLabel
            mediaSize={usedSize ? MEDIA_SIZE_MAP[usedSize] : MEDIA_SIZE_160}
            bgColor={mediaSource!.bgColorOverrided}
            isCompact={isRenderLabelCompact}
          />
        )}
      </div>
    );
  }

  return (
    <MediaPlaceholder
      className={cls(styles.mediaPreview, usedSize && MEDIA_PREVIEW_SIZE_CLASS_MAP[usedSize], className)}
      shape={getProductShapeIcon(product)}
      style={{ order: index }}
    />
  );
}

export default observer(MediaPreview);
